import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Plus, X, Trash2, Lightbulb, Shield } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  open: { label: "Open", color: "text-white/60", bg: "bg-white/5" },
  planned: { label: "Planned", color: "text-[#00F0FF]", bg: "bg-[#00F0FF]/10" },
  building: { label: "Building", color: "text-[#FFCC00]", bg: "bg-[#FFCC00]/15" },
  shipped: { label: "Shipped", color: "text-[#34C759]", bg: "bg-[#34C759]/10" },
  declined: { label: "Declined", color: "text-white/30", bg: "bg-white/5" },
};

export default function Ideas() {
  const { user } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ideas`, { withCredentials: true });
      setIdeas(r.data.ideas || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ideas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = filter === "all" ? ideas : ideas.filter((i) => i.status === filter);

  return (
    <AppLayout>
      <div className="px-4 sm:px-6 py-6 max-w-3xl mx-auto" data-testid="ideas-page">
        <div className="flex items-center gap-3 mb-2">
          <Lightbulb className="w-5 h-5 text-[#FFCC00]" />
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight">Ideas</h1>
        </div>
        <p className="text-sm text-white/50 mb-6">
          Pitch new features, vote up the ones you want most. Admins triage and tag what's coming.
        </p>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5 mb-4 text-[11px] tracking-[0.2em] uppercase" data-testid="ideas-filter">
          {["all", "open", "planned", "building", "shipped", "declined"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`ideas-filter-${f}`}
              className={`px-3 py-1.5 border transition-colors ${
                filter === f
                  ? "border-[#FFCC00] text-[#FFCC00] bg-[#FFCC00]/10"
                  : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
          <button
            onClick={() => setShowCompose(true)}
            data-testid="ideas-new-btn"
            className="ml-auto px-3 py-1.5 bg-[#FFCC00] text-black font-heading flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            New idea
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-white/40 text-[11px] tracking-[0.3em] uppercase">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 text-white/40" data-testid="ideas-empty">
            No ideas yet. <button onClick={() => setShowCompose(true)} className="text-[#FFCC00] underline">Be the first!</button>
          </div>
        ) : (
          <ul className="space-y-3">
            {visible.map((idea) => (
              <IdeaRow
                key={idea.idea_id}
                idea={idea}
                me={user}
                onChange={load}
              />
            ))}
          </ul>
        )}
      </div>

      {showCompose && (
        <ComposeModal
          onClose={() => setShowCompose(false)}
          onSubmitted={(idea) => {
            setIdeas((prev) => [idea, ...prev]);
            setShowCompose(false);
          }}
        />
      )}
    </AppLayout>
  );
}

function IdeaRow({ idea, me, onChange }) {
  const [busy, setBusy] = useState(false);
  const [voteState, setVoteState] = useState({
    score: idea.score || 0,
    my_vote: idea.my_vote || 0,
  });
  const status = STATUS_META[idea.status] || STATUS_META.open;
  const isAuthor = me?.user_id === idea.author_id;
  const isAdmin = me?.is_admin;

  const setVote = async (next) => {
    if (busy) return;
    setBusy(true);
    const value = voteState.my_vote === next ? 0 : next;
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/ideas/${idea.idea_id}/vote`,
        { value },
        { withCredentials: true },
      );
      setVoteState({ score: r.data.score, my_vote: r.data.my_vote });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!window.confirm("Withdraw your idea?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/ideas/${idea.idea_id}`, { withCredentials: true });
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed");
    }
  };

  const setStatus = async (newStatus) => {
    const note = window.prompt(`Optional note for "${newStatus}":`, "");
    if (note === null) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/ideas/${idea.idea_id}`,
        { status: newStatus, note: note || null },
        { withCredentials: true },
      );
      toast.success(`Marked ${newStatus}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <li
      className="border border-white/10 bg-black/40 backdrop-blur-md flex"
      data-testid={`idea-row-${idea.idea_id}`}
    >
      {/* Vote rail */}
      <div className="flex flex-col items-center justify-center px-3 py-4 border-r border-white/10">
        <button
          onClick={() => setVote(1)}
          disabled={busy}
          data-testid={`idea-up-${idea.idea_id}`}
          className={`p-1 transition-colors ${voteState.my_vote === 1 ? "text-[#34C759]" : "text-white/40 hover:text-white"}`}
          aria-label="Upvote"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
        <div className={`font-mono text-sm font-bold tabular-nums ${voteState.score > 0 ? "text-[#34C759]" : voteState.score < 0 ? "text-[#FF3B30]" : "text-white/60"}`} data-testid={`idea-score-${idea.idea_id}`}>
          {voteState.score}
        </div>
        <button
          onClick={() => setVote(-1)}
          disabled={busy}
          data-testid={`idea-down-${idea.idea_id}`}
          className={`p-1 transition-colors ${voteState.my_vote === -1 ? "text-[#FF3B30]" : "text-white/40 hover:text-white"}`}
          aria-label="Downvote"
        >
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 p-4 min-w-0">
        <div className="flex items-start gap-2 mb-2">
          <h3 className="font-heading text-base sm:text-lg uppercase tracking-tight flex-1">
            {idea.title}
          </h3>
          <span className={`px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase ${status.color} ${status.bg}`}>
            {status.label}
          </span>
        </div>
        {idea.body && (
          <p className="text-sm text-white/70 whitespace-pre-wrap mb-3">{idea.body}</p>
        )}
        {idea.admin_note && (
          <div className="text-[11px] border border-[#FFCC00]/30 bg-[#FFCC00]/5 text-[#FFCC00] px-2 py-1.5 mb-3">
            <Shield className="w-3 h-3 inline mr-1 -mt-0.5" />
            <b>Admin:</b> {idea.admin_note}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-white/40">
          <span>by <b className="text-white/70">{idea.author_name}</b></span>
          <span className="text-white/20">·</span>
          <span>{idea.up} up · {idea.down} down</span>
          <span className="ml-auto flex items-center gap-2">
            {(isAuthor || isAdmin) && (
              <button
                onClick={withdraw}
                data-testid={`idea-withdraw-${idea.idea_id}`}
                className="text-white/40 hover:text-[#FF3B30]"
                title={isAdmin ? "Delete (admin)" : "Withdraw"}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
            {isAdmin && (
              <select
                onChange={(e) => { if (e.target.value) setStatus(e.target.value); e.target.value = ""; }}
                data-testid={`idea-set-status-${idea.idea_id}`}
                className="bg-black/60 border border-white/10 px-1 py-0.5 text-[10px]"
                defaultValue=""
              >
                <option value="">Set status…</option>
                {Object.keys(STATUS_META).map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
            )}
          </span>
        </div>
      </div>
    </li>
  );
}

function ComposeModal({ onClose, onSubmitted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (title.trim().length < 4) {
      toast.error("Title must be at least 4 characters");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/ideas`,
        { title: title.trim(), body: body.trim() },
        { withCredentials: true },
      );
      toast.success("Idea posted!");
      onSubmitted(r.data.idea);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="ideas-compose-modal"
    >
      <div className="w-full max-w-lg bg-black/95 border border-white/15 p-5">
        <div className="flex items-center gap-3 mb-4">
          <Lightbulb className="w-4 h-4 text-[#FFCC00]" />
          <h2 className="font-heading text-lg uppercase tracking-tight flex-1">New Idea</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <label className="block mb-3">
          <span className="text-[11px] tracking-[0.2em] uppercase text-white/50">Title</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            data-testid="ideas-compose-title"
            className="w-full mt-1 px-3 py-2 bg-black/40 border border-white/10 focus:border-[#FFCC00] focus:outline-none text-sm"
            placeholder="Add a fog effect to London nights"
          />
        </label>
        <label className="block mb-4">
          <span className="text-[11px] tracking-[0.2em] uppercase text-white/50">Details (optional)</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            rows={5}
            data-testid="ideas-compose-body"
            className="w-full mt-1 px-3 py-2 bg-black/40 border border-white/10 focus:border-[#FFCC00] focus:outline-none text-sm font-mono"
            placeholder="Describe how it'd work, why it'd be cool, etc."
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="ideas-compose-cancel"
            className="px-4 py-2 border border-white/10 text-sm hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="ideas-compose-submit"
            className="px-4 py-2 bg-[#FFCC00] text-black font-heading uppercase text-sm disabled:opacity-40"
          >
            {busy ? "Posting…" : "Post idea"}
          </button>
        </div>
      </div>
    </div>
  );
}
